/**
 * Auth state management — JWT + MFA
 */
import { create } from 'zustand'
import { api } from './api'

interface AuthState {
  token: string | null
  email: string | null
  isAuthenticated: boolean
  mfaRequired: boolean
  loading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<boolean>
  verifyMFA: (email: string, password: string, code: string) => Promise<boolean>
  loginWithToken: (token: string, email: string) => void
  logout: () => void
  checkAuth: () => void
}

export const useAuth = create<AuthState>((set, get) => ({
  token: localStorage.getItem('cto-token'),
  email: localStorage.getItem('cto-email'),
  isAuthenticated: !!localStorage.getItem('cto-token'),
  mfaRequired: false,
  loading: false,
  error: null,

  login: async (email: string, password: string) => {
    set({ loading: true, error: null })
    try {
      const { data } = await api.post('/api/auth/login', { email, password })
      if (data.requires_mfa) {
        set({ mfaRequired: true, loading: false })
        return true
      }
      return false
    } catch (e: any) {
      set({ error: e.response?.data?.detail || 'Login failed', loading: false })
      return false
    }
  },

  verifyMFA: async (email: string, password: string, code: string) => {
    set({ loading: true, error: null })
    try {
      const { data } = await api.post('/api/auth/verify-mfa', { email, password, totp_code: code })
      localStorage.setItem('cto-token', data.access_token)
      localStorage.setItem('cto-email', data.email)
      set({ token: data.access_token, email: data.email, isAuthenticated: true, mfaRequired: false, loading: false })

      // Set default Authorization header for all future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`
      return true
    } catch (e: any) {
      set({ error: e.response?.data?.detail || 'MFA verification failed', loading: false })
      return false
    }
  },

  loginWithToken: (token: string, email: string) => {
    localStorage.setItem('cto-token', token)
    localStorage.setItem('cto-email', email)
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`
    set({ token, email, isAuthenticated: true, mfaRequired: false, loading: false, error: null })
  },

  logout: () => {
    localStorage.removeItem('cto-token')
    localStorage.removeItem('cto-email')
    delete api.defaults.headers.common['Authorization']
    set({ token: null, email: null, isAuthenticated: false, mfaRequired: false })
  },

  checkAuth: () => {
    const token = localStorage.getItem('cto-token')
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`
      set({ token, email: localStorage.getItem('cto-email'), isAuthenticated: true })
    }
  },
}))
